import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import EditIcon from '@mui/icons-material/Edit';
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";

import ButtonComponent from "../../../components/common/button/Button";
import DataTable from "../../../components/common/datatable/DataTable";

const DataDetails = (props) => {
  const priceMemoData = useSelector(
    (state) => state?.piceMemoData
  );
  const [disableFilterBtn, setDisableFilterBtn] = useState(true);
  const [filteredTotalItems, setFilteredTotalItems] = useState(priceMemoData?.data?.totalNumberOfItems);
  const [filteredTotalColumn, setFilteredTotalColumn] = useState(0);
  const [isGridReady, setIsGridReady] = useState(false);
  const [columnState, setColumnState] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState('');

  const handlePostFilterChanged = (filteredCount, totalCount) => {
    setFilteredTotalItems(filteredCount); // Update the filtered total items
  };

  const isInvalidValue = (value) => {
    return value === null || value === undefined || value === "" || value === "-";
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "-";
    return dayjs(timestamp).format("DD-MMM-YYYY HH:mm:ss");
  };

  const renderExpandableText = (params, maxLength = 50) => {
    const text = params.value || ""; // The error details text
    const isLongText = text.length > maxLength;

    // Initialize the showFullText property for the row if not already set
    if (!params.node.showFullText) {
      params.node.showFullText = false;
    }

    const toggleText = () => {
      params.node.showFullText = !params.node.showFullText;
      params.api.refreshCells({ rowNodes: [params.node], force: true }); // Refresh the cell to update the UI
    };

    return isLongText ? (
      <div>
        {params.node.showFullText ? (
          <>
            <span>{text}</span>{" "}
            <button
              onClick={toggleText}
              style={{
                background: "none",
                border: "none",
                color: "#181d1f",
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: "bold",
              }}
            >
              Less
            </button>
          </>
        ) : (
          <>
            <span>{text.substring(0, maxLength)}...</span>{" "}
            <button
              onClick={toggleText}
              style={{
                background: "none",
                border: "none",
                color: "#181d1f",
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: "bold",
              }}
            >
              More
            </button>
          </>
        )}
      </div>
    ) : (
      <span>{text}</span>
    );
  };

  const columnData = useMemo(() => {
    if(props.data.sisPriceMemo) {
      return [
        {
          headerName: 'Is New',
          field: "isNew",
          sortable: true,
          resizable: false,
          filterParams: {
            textFormatter: (value) => {
              if (typeof value === "string") {
                const v = value.trim().toLowerCase();
                if (v === "!") return "!";
                if (v === "-") return "-";
                return v;
              }
              return value;
            },
          },
          valueGetter: (params) => {
            if (params.data?.isNew === true) return "!";
            if (params.data?.isNew === false) return "-";
            return "";
          },
          valueFormatter: (params) => params.value,
          cellRenderer: (params) => {
            if (params.value === "!") {
              return (
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "20px",
                    textAlign: "center",
                  }}
                >
                  !
                </span>
              );
            }
            if (params.value === "-") {
              return (
                <span
                  style={{
                    color: "#616161",
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                >
                  -
                </span>
              );
            }
            return "";
          },
          pinned: "left",
          unSortIcon: true,
          width: 170,
        },
        {
          headerName: 'Ingredient',
          field: "ingredient",
          sortable: true,
          resizable: false,
          pinned: "left",
          unSortIcon: true,
          width: 150,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('ingredient')
          }
        },
        {
          headerName: 'Package Size',
          field: "packSize",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('packSize')
          }
        },
        {
          headerName: 'Sap Plant Number',
          field: "sapPlantId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: (params) => 
            params?.data?.status?.toLowerCase().includes("contract submitted") ||
            params?.data?.status?.toLowerCase().includes("contract creation failed") ||
            params?.data?.status?.toLowerCase().includes("contract update successful") ||
            params?.data?.status?.toLowerCase().includes("news") ||
            params?.data?.status?.toLowerCase().includes("contract simulation - success") ||
            params?.data?.status?.toLowerCase().includes("contract creation successful") ||
            params?.data?.status?.toLowerCase().includes("contract simulation - failed") ||
            props.userRole === "Viewer"
            ? false : true,
          cellEditor: "agTextCellEditor",
          cellClass: props.userRole !== "Viewer" && "cpsComments-cell",
          // onCellValueChanged: (params) =>
          //   props.handleCellValueChange(params, "sapPlantId"),
          // valueSetter: (params) =>
          //   props.handleCellValueChange(params, "sapPlantId"),
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, sapPlantId: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
              </div>
            );
          },
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('sapPlantId'),
            "red-cell": (params) => {
                const value = params.value ? params.value.toString().trim() : "";
                return !value || value === "-";
              },
          }
        },
        {
          headerName: 'Plant Name',
          field: "plantName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('plantName')
          }
        },
        {
          headerName: 'Sap Material Number',
          field: "sapMaterialNumber",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: (params) => 
            params?.data?.status?.toLowerCase().includes("contract submitted") ||
            params?.data?.status?.toLowerCase().includes("contract creation failed") ||
            params?.data?.status?.toLowerCase().includes("contract update successful") ||
            params?.data?.status?.toLowerCase().includes("news") ||
            params?.data?.status?.toLowerCase().includes("contract simulation - success") ||
            params?.data?.status?.toLowerCase().includes("contract creation successful") ||
            params?.data?.status?.toLowerCase().includes("contract simulation - failed") ||
            props.userRole === "Viewer"
            ? false : true,
          cellEditor: "agTextCellEditor",
          cellClass: props.userRole !== "Viewer" && "cpsComments-cell",
          // onCellValueChanged: (params) =>
          //   props.handleCellValueChange(params, "sapMaterialNumber"),
          // valueSetter: (params) =>
          //   props.handleCellValueChange(params, "sapMaterialNumber"),
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, sapMaterialNumber: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
              </div>
            );
          },
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('sapMaterialNumber'),
            // "red-cell": (params) => {
            //     const value = params.value ? params.value.toString().trim() : "";
            //     return !value || value === "-";
            //   },
            "red-cell": (params) => isInvalidValue(params.value),
          }
        },
        {
          headerName: 'Sap Material Description',
          field: "sapMaterialDescription",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('sapMaterialDescription')
          }
        },
        {
          headerName: 'Sap Vendor Number',
          field: "sapSupplierId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: (params) => 
            params?.data?.status?.toLowerCase().includes("contract submitted") ||
            params?.data?.status?.toLowerCase().includes("contract creation failed") ||
            params?.data?.status?.toLowerCase().includes("contract update successful") ||
            params?.data?.status?.toLowerCase().includes("news") ||
            params?.data?.status?.toLowerCase().includes("contract simulation - success") ||
            params?.data?.status?.toLowerCase().includes("contract creation successful") ||
            params?.data?.status?.toLowerCase().includes("contract simulation - failed") ||
            props.userRole === "Viewer"
            ? false : true,
          cellEditor: "agTextCellEditor",
          cellClass: props.userRole !== "Viewer" && "cpsComments-cell",
          // onCellValueChanged: (params) =>
          //   props.handleCellValueChange(params, "sapSupplierId"),
          // valueSetter: (params) =>
          //   props.handleCellValueChange(params, "sapSupplierId"),
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, sapSupplierId: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
              </div>
            );
          },
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('sapSupplierId'),
            "red-cell": (params) => {
                const value = params.value ? params.value.toString().trim() : "";
                return !value || value === "-";
              },
          }
        },
        {
          headerName: 'Sap Vendor Description',
          field: "sapSupplierDescription",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('sapSupplierDescription')
          }
        },
        {
          headerName: 'Agreement Number',
          field: "agreementNumber",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('agreementNumber')
          }
        },
        {
          headerName: 'Agreement Date',
          field: "agreementDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('agreementDate')
          }
        },
        {
          headerName: 'Item Number',
          field: "agreementItemNumber",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('agreementItemNumber')
          }
        },
        {
          headerName: 'S-Code',
          field: "specequivalentcode",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('specequivalentcode')
          }
        },
        {
          headerName: 'Year',
          field: "yearSIS",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('yearSIS')
          }
        },
        {
          headerName: 'Price Tag ID',
          field: "priceTagId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('priceTagId')
          }
        },
        {
          headerName: 'Division',
          field: "division",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('division')
          }
        },
        {
          headerName: 'Change Date',
          field: "changeDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('changeDate')
          },
          // return a JS Date for AG Grid sorting/filtering
          valueGetter: (params) => {
            const v = params.data?.changeDate;
            if (!v) return null;
            const s = typeof v === 'string' ? v.replace(/\.00$/, '') : v;
            const d = new Date(s);
            return isNaN(d.getTime()) ? null : d;
          },

          // display as DD-MM-YYYY (prefer original string when available)
          valueFormatter: (params) => {
            const src = params.data?.changeDate;
            if (src && typeof src === 'string') {
              const s = src.replace(/\.00$/, '');
              const d = new Date(s);
              if (!isNaN(d.getTime())) {
                const dd = String(d.getDate()).padStart(2, '0');
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const yyyy = d.getFullYear();
                return `${dd}-${mm}-${yyyy}`;
              }
              return src;
            }
            if (params.value instanceof Date && !isNaN(params.value.getTime())) {
              const d = params.value;
              const dd = String(d.getDate()).padStart(2, '0');
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const yyyy = d.getFullYear();
              return `${dd}-${mm}-${yyyy}`;
            }
            return "-";
          },

          filter: "agDateColumnFilter",
          filterParams: {
            comparator: (filterLocalDateAtMidnight, cellValue, node) => {
              const src = node?.data?.changeDate ?? cellValue;
              if (!src) return -1;
              const s = typeof src === 'string' ? src.replace(/\.00$/, '') : src;
              const d = new Date(s);
              if (isNaN(d.getTime())) return -1;
              const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
              const filterYMD = new Date(
                filterLocalDateAtMidnight.getFullYear(),
                filterLocalDateAtMidnight.getMonth(),
                filterLocalDateAtMidnight.getDate()
              );
              if (cellYMD.getTime() === filterYMD.getTime()) return 0;
              return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
            },
            browserDatePicker: true,
          },

          // ensure sort parses the original string (works even when valueGetter removed elsewhere)
          sortComparator: (valueA, valueB, nodeA, nodeB) => {
            const aStr = nodeA?.data?.changeDate ?? valueA;
            const bStr = nodeB?.data?.changeDate ?? valueB;
            const aDate = aStr ? new Date((typeof aStr === 'string' ? aStr.replace(/\.00$/, '') : aStr)) : null;
            const bDate = bStr ? new Date((typeof bStr === 'string' ? bStr.replace(/\.00$/, '') : bStr)) : null;
            const ta = aDate && !isNaN(aDate.getTime()) ? aDate.getTime() : null;
            const tb = bDate && !isNaN(bDate.getTime()) ? bDate.getTime() : null;
            if (ta === null && tb === null) return 0;
            if (ta === null) return -1;
            if (tb === null) return 1;
            return ta - tb;
          },
        },
        {
          headerName: 'Plant Id',
          field: "plantId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('plantId')
          }
        },
        {
          headerName: 'Sap Plant Number',
          field: "sapPlantId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: (params) => 
            params?.data?.status?.toLowerCase().includes("contract submitted") ||
            params?.data?.status?.toLowerCase().includes("contract creation failed") ||
            params?.data?.status?.toLowerCase().includes("contract update successful") ||
            params?.data?.status?.toLowerCase().includes("news") ||
            params?.data?.status?.toLowerCase().includes("contract simulation - success") ||
            params?.data?.status?.toLowerCase().includes("contract creation successful") ||
            params?.data?.status?.toLowerCase().includes("contract simulation - failed") ||
            props.userRole === "Viewer"
            ? false : true,
          cellEditor: "agTextCellEditor",
          cellClass: props.userRole !== "Viewer" && "cpsComments-cell",
          // onCellValueChanged: (params) =>
          //   props.handleCellValueChange(params, "sapPlantId"),
          // valueSetter: (params) =>
          //   props.handleCellValueChange(params, "sapPlantId"),
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, sapPlantId: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
              </div>
            );
          },
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('sapPlantId'),
            "red-cell": (params) => {
                const value = params.value ? params.value.toString().trim() : "";
                return !value || value === "-";
              },
          }
        },
        {
          headerName: 'Plant Name',
          field: "plantName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('plantName')
          }
        },
        {
          headerName: 'Supplier Id',
          field: "supplierId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('supplierId')
          }
        },
        {
          headerName: 'Sap Vendor Number',
          field: "sapSupplierId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: (params) => 
            params?.data?.status?.toLowerCase().includes("contract submitted") ||
            params?.data?.status?.toLowerCase().includes("contract creation failed") ||
            params?.data?.status?.toLowerCase().includes("contract update successful") ||
            params?.data?.status?.toLowerCase().includes("news") ||
            params?.data?.status?.toLowerCase().includes("contract simulation - success") ||
            params?.data?.status?.toLowerCase().includes("contract creation successful") ||
            params?.data?.status?.toLowerCase().includes("contract simulation - failed") ||
            props.userRole === "Viewer"
            ? false : true,
          cellEditor: "agTextCellEditor",
          cellClass: props.userRole !== "Viewer" && "cpsComments-cell",
          // onCellValueChanged: (params) =>
          //   props.handleCellValueChange(params, "sapSupplierId"),
          // valueSetter: (params) =>
          //   props.handleCellValueChange(params, "sapSupplierId"),
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, sapSupplierId: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
              </div>
            );
          },
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('sapSupplierId'),
            "red-cell": (params) => {
                const value = params.value ? params.value.toString().trim() : "";
                return !value || value === "-";
              },
          }
        },
        {
          headerName: 'Sap Vendor Description',
          field: "sapSupplierDescription",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('sapSupplierDescription')
          }
        },
        {
          headerName: 'Supplier',
          field: "supplierName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('supplierName')
          }
        },
        {
          headerName: 'Location Name',
          field: "locationName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('locationName')
          }
        },
        {
          headerName: 'Supplier Country',
          field: "facilitycountry",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('facilitycountry')
          }
        },
        {
          headerName: 'PIN',
          field: "pin",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('pin')
          }
        },
        {
          headerName: 'Sap Material Number',
          field: "sapMaterialNumber",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: (params) => 
            params?.data?.status?.toLowerCase().includes("contract submitted") ||
            params?.data?.status?.toLowerCase().includes("contract creation failed") ||
            params?.data?.status?.toLowerCase().includes("contract update successful") ||
            params?.data?.status?.toLowerCase().includes("news") ||
            params?.data?.status?.toLowerCase().includes("contract simulation - success") ||
            params?.data?.status?.toLowerCase().includes("contract creation successful") ||
            params?.data?.status?.toLowerCase().includes("contract simulation - failed") ||
            props.userRole === "Viewer"
            ? false : true,
          cellEditor: "agTextCellEditor",
          cellClass: props.userRole !== "Viewer" && "cpsComments-cell",
          // onCellValueChanged: (params) =>
          //   props.handleCellValueChange(params, "sapMaterialNumber"),
          // valueSetter: (params) =>
          //   props.handleCellValueChange(params, "sapMaterialNumber"),
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, sapMaterialNumber: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
              </div>
            );
          },
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('sapMaterialNumber'),
            // "red-cell": (params) => {
            //     const value = params.value ? params.value.toString().trim() : "";
            //     return !value || value === "-";
            //   },
            "red-cell": (params) => isInvalidValue(params.value),
          }
        },
        {
          headerName: 'PIN',
          field: "pin",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('pin')
          }
        },
        {
          headerName: 'Material Group',
          field: "extmatgrp",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('extmatgrp')
          }
        },
        {
          headerName: 'Package Name',
          field: "packageName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('packageName')
          }
        },
        {
          headerName: 'Bulk Flag',
          field: "isbulk",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('isbulk')
          }
        },
        {
          headerName: 'UOM',
          field: "uom",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('uom')
          }
        },
        {
          headerName: 'Price per Kg',
          field: "pricePerKG",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('pricePerKG')
          }
        },
        {
          headerName: 'Currency',
          field: "currency",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('currency')
          }
        },
        {
          headerName: 'Effective From',
          field: "effectiveStartDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('effectiveStartDate')
          },
          // provide a JS Date for AG Grid sorting/filtering
          valueGetter: (params) => {
            const v = params.data?.effectiveStartDate;
            if (!v || typeof v !== 'string') return null;
            const parts = v.split('-'); // expecting "DD-MM-YYYY"
            if (parts.length !== 3) return null;
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            const d = new Date(year, month, day);
            return isNaN(d.getTime()) ? null : d;
          },

          // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
          valueFormatter: (params) => {
            const src = params.data?.effectiveStartDate;
            if (src && typeof src === 'string') return src;
            if (params.value instanceof Date && !isNaN(params.value.getTime())) {
              const d = params.value;
              const dd = String(d.getDate()).padStart(2, '0');
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const yyyy = d.getFullYear();
              return `${dd}-${mm}-${yyyy}`;
            }
            return "-";
          },

          filter: "agDateColumnFilter",

          // sort by parsing the source string from the row data (robust even if other getters exist)
          sortComparator: (valueA, valueB, nodeA, nodeB) => {
            const aStr = nodeA?.data?.effectiveStartDate ?? valueA;
            const bStr = nodeB?.data?.effectiveStartDate ?? valueB;
            const parse = (s) => {
              if (!s || typeof s !== 'string') return null;
              const p = s.split('-');
              if (p.length !== 3) return null;
              const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
              return isNaN(d.getTime()) ? null : d.getTime();
            };
            const ta = parse(aStr);
            const tb = parse(bStr);
            if (ta === null && tb === null) return 0;
            if (ta === null) return -1;
            if (tb === null) return 1;
            return ta - tb;
          },
        },
        {
          headerName: 'Effective To',
          field: "effectiveEndDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('effectiveEndDate')
          },
          // provide a JS Date for AG Grid sorting/filtering
          valueGetter: (params) => {
            const v = params.data?.effectiveEndDate;
            if (!v || typeof v !== 'string') return null;
            const parts = v.split('-'); // expecting "DD-MM-YYYY"
            if (parts.length !== 3) return null;
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            const d = new Date(year, month, day);
            return isNaN(d.getTime()) ? null : d;
          },

          // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
          valueFormatter: (params) => {
            const src = params.data?.effectiveEndDate;
            if (src && typeof src === 'string') return src;
            if (params.value instanceof Date && !isNaN(params.value.getTime())) {
              const d = params.value;
              const dd = String(d.getDate()).padStart(2, '0');
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const yyyy = d.getFullYear();
              return `${dd}-${mm}-${yyyy}`;
            }
            return "-";
          },

          filter: "agDateColumnFilter",
          // sort by parsing the source string from the row data (robust even if other getters exist)
          sortComparator: (valueA, valueB, nodeA, nodeB) => {
            const aStr = nodeA?.data?.effectiveEndDate ?? valueA;
            const bStr = nodeB?.data?.effectiveEndDate ?? valueB;
            const parse = (s) => {
              if (!s || typeof s !== 'string') return null;
              const p = s.split('-');
              if (p.length !== 3) return null;
              const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
              return isNaN(d.getTime()) ? null : d.getTime();
            };
            const ta = parse(aStr);
            const tb = parse(bStr);
            if (ta === null && tb === null) return 0;
            if (ta === null) return -1;
            if (tb === null) return 1;
            return ta - tb;
          },
        },
        {
          headerName: 'Allocation Quantity',
          field: "allocationQty",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('allocationQty')
          }
        },
        {
          headerName: 'Allocation Notes',
          field: "allocationNotes",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('allocationNotes')
          }
        },
        {
          headerName: 'From (Qty)',
          field: "fromQty",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('fromQty')
          }
        },
        {
          headerName: 'To (Qty)',
          field: "toQty",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('toQty')
          }
        },
        {
          headerName: 'MOQ Comments',
          field: "moqComments",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellRenderer: (params) => renderExpandableText(params, 50),
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('moqComments')
          }
        },
        {
          headerName: 'Payment Term',
          field: "paymentTerms",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('paymentTerms')
          }
        },
        {
          headerName: 'Manufacturing Lead Time',
          field: "manufacturingLeadTime",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('manufacturingLeadTime')
          }
        },
        {
          headerName: 'Auto Co Ship',
          field: "autoCoship",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('autoCoship')
          }
        },
        {
          headerName: 'Inco Terms',
          field: "incoterms",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('incoterms')
          }
        },
        {
          headerName: 'Named Place',
          field: "namedPlace",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('namedPlace')
          }
        },
        {
          headerName: 'Supply Point Number',
          field: "supplypointid",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('supplypointid')
          }
        },
        {
          headerName: 'Supply Point Description',
          field: "supplypointname",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('supplypointname')
          }
        },
        {
          headerName: 'Manager Initials',
          field: "managerInitials",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('managerInitials')
          }
        },
        {
          headerName: 'Manager',
          field: "managerName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('managerName')
          }
        },
        {
          headerName: 'PC Initial',
          field: "pcInitial",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('pcInitial')
          }
        },
        {
          headerName: 'SCA',
          field: "sca",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('sca')
          }
        },
        {
          headerName: 'PMU Date',
          field: "pmudate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('pmudate')
          },
          valueFormatter: (params) => formatTimestamp(params.value)
        },
        {
          headerName: 'Price Per Lb',
          field: "pricePerLb",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('pricePerLb')
          }
        },
        {
          headerName: 'Manufacturing Facility Number',
          field: "facilitynum",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('facilitynum')
          }
        },
        {
          headerName: 'Manufacturing Facility Description',
          field: "facilityname",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('facilityname')
          }
        },
        // {
        //   headerName: 'X',
        //   field: "x",
        //   sortable: true,
        //   resizable: false,
        //   filterParams: commonFilterParams,
        //   filter: commonFilter,
        //   suppressMovable: true,
        //   unSortIcon: true,
        //   cellClassRules: {
        //     'bold-cell': (params) => params.data.changedFieldNames?.includes('x')
        //   }
        // },
        {
          headerName: "Religious Attributes",
          headerClass: "center-header",
          children: [
            {
              headerName: 'Halal',
              field: "halal",
              sortable: true,
              resizable: false,
              unSortIcon: true,
              cellClassRules: {
                'bold-cell': (params) => params.data.changedFieldNames?.includes('halal')
              }
            },
            {
              headerName: 'Kosher PO',
              field: "kosherPO",
              sortable: true,
              resizable: false,
              unSortIcon: true,
              cellClassRules: {
                'bold-cell': (params) => params.data.changedFieldNames?.includes('kosherPO')
              }
            },
            {
              headerName: 'Kosher Yr',
              field: "kosherYr",
              sortable: true,
              resizable: false,
              unSortIcon: true,
              cellClassRules: {
                'bold-cell': (params) => params.data.changedFieldNames?.includes('kosherYr')
              }
            },
            {
              headerName: 'Kosher',
              field: "kosherstatus",
              sortable: true,
              resizable: false,
              unSortIcon: true,
              cellClassRules: {
                'bold-cell': (params) => params.data.changedFieldNames?.includes('kosherstatus')
              }
            },
            {
              headerName: 'Kosher Passover',
              field: "kosherkfpner",
              sortable: true,
              resizable: false,
              unSortIcon: true,
              cellClassRules: {
                'bold-cell': (params) => params.data.changedFieldNames?.includes('kosherkfpner')
              }
            },
            {
              headerName: 'Kosher Passover (OU)',
              field: "kosherkfpou",
              sortable: true,
              resizable: false,
              unSortIcon: true,
              cellClassRules: {
                'bold-cell': (params) => params.data.changedFieldNames?.includes('kosherkfpou')
              }
            },
            {
              headerName: 'Kosher Year (NER)',
              field: "kosherkyrner",
              sortable: true,
              resizable: false,
              unSortIcon: true,
              cellClassRules: {
                'bold-cell': (params) => params.data.changedFieldNames?.includes('kosherkyrner')
              }
            },
            {
              headerName: 'Kosher Year (OU)',
              field: "kosherkyrou",
              sortable: true,
              resizable: false,
              unSortIcon: true,
              cellClassRules: {
                'bold-cell': (params) => params.data.changedFieldNames?.includes('kosherkyrou')
              }
            },
            {
              headerName: 'Allergen',
              field: "allergenstatus",
              sortable: true,
              resizable: false,
              unSortIcon: true,
              cellClassRules: {
                'bold-cell': (params) => params.data.changedFieldNames?.includes('allergenstatus')
              }
            },
            {
              headerName: 'Allergen Description',
              field: "allergenknown",
              sortable: true,
              resizable: false,
              unSortIcon: true,
              cellClassRules: {
                'bold-cell': (params) => params.data.changedFieldNames?.includes('allergenknown')
              }
            },
            {
              headerName: 'Fairtrade',
              field: "fairtradecertified",
              sortable: true,
              resizable: false,
              unSortIcon: true,
              cellClassRules: {
                'bold-cell': (params) => params.data.changedFieldNames?.includes('fairtradecertified')
              }
            },
          ]
        },
        {
          headerName: 'Forecast',
          field: "foreCastFile",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('foreCastFile')
          }
        },
        {
          headerName: 'Remarks',
          field: "mappingRemarks",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          // cellClass: "paymentTerms-cell",
          cellRenderer: (params) => renderExpandableText(params, 50),
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('mappingRemarks')
          }
        },
        {
          headerName: 'Status',
          pinned: "right",
          field: "status",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('status')
          }
        },
        {
          headerName: 'Reject',
          pinned: "right",
          field: "reject",
          sortable: false,
          resizable: false,
          unSortIcon: true,
          width: 120,
          filter: false,
          hide: props.userRole === "Viewer" ? true : false,
          cellRenderer: (params) => (
            <ButtonComponent
              maxWidth="100px"
              label="Reject"
              bgColor="black"
              color="white"
              disabled={
                params?.data?.status?.toLowerCase().includes("contract submitted") || 
                params?.data?.status?.toLowerCase().includes("contract creation failed") || 
                params?.data?.status?.toLowerCase().includes("contract update successful") || 
                params?.data?.status?.toLowerCase().includes("news") ||
                params?.data?.status?.toLowerCase().includes("contract simulation - success") ||
                params?.data?.status?.toLowerCase().includes("contract creation successful") ||
                params?.data?.status?.toLowerCase().includes("contract simulation - failed")
                ? true : false}
              onClick={() => props.rejectHandler(params?.data?.runOutputSeqId)}
            />
          ),
        },
      ]
    } else if (props.data.agriPriceMemo) {
      return [
        {
          headerName: 'Is New',
          field: "isNew",
          sortable: true,
          resizable: false,
          filterParams: {
            textFormatter: (value) => {
              if (typeof value === "string") {
                const v = value.trim().toLowerCase();
                if (v === "!") return "!";
                if (v === "-") return "-";
                return v;
              }
              return value;
            },
          },
          valueGetter: (params) => {
            if (params.data?.isNew === true) return "!";
            if (params.data?.isNew === false) return "-";
            return "";
          },
          valueFormatter: (params) => params.value,
          cellRenderer: (params) => {
            if (params.value === "!") {
              return (
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "20px",
                    textAlign: "center",
                  }}
                >
                  !
                </span>
              );
            }
            if (params.value === "-") {
              return (
                <span
                  style={{
                    color: "#616161",
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                >
                  -
                </span>
              );
            }
            return "";
          },
          pinned: "left",
          unSortIcon: true,
          width: 130,
        },
        {
          headerName: 'Plant Id',
          field: "plantId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('plantId')
          }
        },
        {
          headerName: 'Vendor Number',
          field: "vendorNumber",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('vendorNumber')
          }
        },
        {
          headerName: 'Material Number',
          field: "materialNumber",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('materialNumber')
          }
        },
        {
          headerName: 'Plant Name',
          field: "plantName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('plantName')
          }
        },
        {
          headerName: 'Supplier',
          field: "supplierName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('supplierName')
          }
        },
        {
          headerName: 'Material Description',
          field: "materialDescription",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('materialDescription')
          }
        },
        {
          headerName: 'S No.',
          field: "snumber",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('snumber')
          }
        },
        {
          headerName: 'Currency',
          field: "currency",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('currency')
          }
        },
        {
          headerName: 'Volume',
          field: "volume",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('volume')
          }
        },
        {
          headerName: 'Group',
          field: "groupAgri",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('groupAgri')
          }
        },
        {
          headerName: 'Sub Category',
          field: "subCategory",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('subCategory')
          }
        },
        {
          headerName: 'Brand',
          field: "brand",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('brand')
          }
        },
        {
          headerName: 'Prorement Manager',
          field: "prorementManager",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('prorementManager')
          }
        },
        {
          headerName: 'UOM',
          field: "uom",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('uom')
          }
        },
        {
          headerName: 'Geography',
          field: "geography",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('geography')
          }
        },
        {
          headerName: 'Picasso Approval',
          field: "picassoApproval",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('picassoApproval')
          }
        },
        {
          headerName: 'Material Origin',
          field: "materialOrigin",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('materialOrigin')
          }
        },
        {
          headerName: 'LeadTime',
          field: "leadTime",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('leadTime')
          }
        },
        {
          headerName: 'Ship From Country',
          field: "shipFromCountry",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('shipFromCountry')
          }
        },
        {
          headerName: 'Ship From City',
          field: "shipFromCity",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('shipFromCity')
          }
        },
        {
          headerName: 'Payment Term',
          field: "paymentTerm",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('paymentTerm')
          }
        },
        {
          headerName: 'Material Cost Per UOM',
          field: "materialCostPerUOM",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('materialCostPerUOM')
          }
        },
        {
          headerName: 'Total Cost Per UOM',
          field: "totalCostPerUOM",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('totalCostPerUOM')
          }
        },
        {
          headerName: 'MOQ Comments',
          field: "moqComments",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell status-cell",
          cellRenderer: (params) => renderExpandableText(params, 50),
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('moqComments')
          }
        },
        {
          headerName: 'RFA Ft Per UOM',
          field: "rfaFtPerUOM",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('rfaFtPerUOM')
          }
        },
        {
          headerName: 'Bid Incoterm',
          field: "bidIncoterm",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('bidIncoterm')
          }
        },
        // {
        //   headerName: 'Open PO',
        //   field: "openPO",
        //   sortable: true,
        //   resizable: false,
        //   filterParams: commonFilterParams,
        //   filter: commonFilter,
        //   suppressMovable: true,
        //   unSortIcon: true,
        //   cellClassRules: {
        //     'bold-cell': (params) => params.data.changedFieldNames?.includes('openPO')
        //   }
        // },
        {
          headerName: 'Bid Incoterm Location',
          field: "bidIncotermLocation",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('bidIncotermLocation')
          }
        },
        {
          headerName: 'Price Effective Date',
          field: "priceEffectiveDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('priceEffectiveDate')
          }
        },
        {
          headerName: 'Price EndDate',
          field: "priceEndDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('priceEndDate')
          }
        },
        {
          headerName: 'Packaging Size',
          field: "packagingSize",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('packagingSize')
          }
        },
        {
          headerName: 'Forecast',
          field: "foreCastFile",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('foreCastFile')
          }
        },
        {
          headerName: 'Status',
          pinned: "right",
          field: "status",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('status')
          }
        },
        {
          headerName: 'Reject',
          pinned: "right",
          field: "reject",
          sortable: false,
          resizable: false,
          unSortIcon: true,
          filter: false,
          width: 120,
          cellRenderer: (params) => (
            <ButtonComponent
              maxWidth="100px"
              label="Reject"
              bgColor="black"
              color="white"
              disabled={
                params?.data?.status?.toLowerCase().includes("contract submitted") || 
                params?.data?.status?.toLowerCase().includes("contract creation failed") || 
                params?.data?.status?.toLowerCase().includes("contract update successful") || 
                params?.data?.status?.toLowerCase().includes("news") ||
                params?.data?.status?.toLowerCase().includes("contract simulation - success") ||
                params?.data?.status?.toLowerCase().includes("contract creation successful") ||
                params?.data?.status?.toLowerCase().includes("contract simulation - failed")
                ? true : false}
              onClick={() => props.rejectHandler(params?.data?.runOutputSeqId)}
            />
          ),
        }
      ]
    } else if (props.data.packagingPriceMemo) {
      return [
        {
          headerName: 'Is New',
          field: "isNew",
          sortable: true,
          resizable: false,
          filterParams: {
            textFormatter: (value) => {
              if (typeof value === "string") {
                const v = value.trim().toLowerCase();
                if (v === "!") return "!";
                if (v === "-") return "-";
                return v;
              }
              return value;
            },
          },
          valueGetter: (params) => {
            if (params.data?.isNew === true) return "!";
            if (params.data?.isNew === false) return "-";
            return "";
          },
          valueFormatter: (params) => params.value,
          cellRenderer: (params) => {
            if (params.value === "!") {
              return (
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "20px",
                    textAlign: "center",
                  }}
                >
                  !
                </span>
              );
            }
            if (params.value === "-") {
              return (
                <span
                  style={{
                    color: "#616161",
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                >
                  -
                </span>
              );
            }
            return "";
          },
          pinned: "left",
          unSortIcon: true,
          width: 130,
        },
        {
          headerName: "Last Updated Time",
          field: "lastUpdateTimestamp",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          filter: "agSetColumnFilter",
          valueFormatter: (params) => formatTimestamp(params.value),
          filterParams: {
            valueFormatter: (params) => formatTimestamp(params.value),
          },
        },
        {
          headerName: 'SAP Plant Number',
          field: "plantId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('plantId')
          }
        },
        {
          headerName: 'Plant Name',
          field: "plantName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('plantName')
          }
        },
        {
          headerName: 'SAP Material Number',
          field: "ccMaterialNo",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('ccMaterialNo')
          }
        },
        {
          headerName: 'Material Description',
          field: "materialDescription",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('materialDescription')
          }
        },
        {
          headerName: 'SAP Vendor Number',
          field: "vendorNumber",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('vendorNumber')
          }
        },
        {
          headerName: 'Supplier Description',
          field: "supplierName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('supplierName')
          }
        },
        {
          headerName: 'Agreement Number',
          field: "agreementNumber",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('agreementNumber')
          }
        },
        {
          headerName: 'Agreement Date',
          field: "agreementDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('agreementDate')
          }
        },
        {
          headerName: 'Item Number',
          field: "agreementItemNumber",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('agreementItemNumber')
          }
        },
        {
          headerName: 'Supplier Item No',
          field: "supplierItemNo",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('supplierItemNo')
          }
        },
        {
          headerName: 'Year',
          field: "yearPckg",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('yearPckg')
          }
        },
        {
          headerName: 'Division',
          field: "division",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('division')
          }
        },
        {
          headerName: 'Location Name',
          field: "locationName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('locationName')
          }
        },
        {
          headerName: 'Manager',
          field: "managerName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('managerName')
          }
        },
        {
          headerName: 'Last Change',
          field: "lastChange",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('lastChange')
          }
        },
        {
          headerName: 'Payment Description',
          field: "paymentDescription",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('paymentDescription')
          }
        },
        {
          headerName: 'Price',
          field: "price",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('price')
          }
        },
        {
          headerName: 'Supplier Description',
          field: "supplierDescription",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('supplierDescription')
          }
        },
        {
          headerName: 'Material Type',
          field: "materialType",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('materialType')
          }
        },
        {
          headerName: 'Currency',
          field: "currency",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('currency')
          }
        },
        {
          headerName: 'UOM',
          field: "uom",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('uom')
          }
        },
        {
          headerName: 'Inco Terms',
          field: "incoterms",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('incoterms')
          }
        },
        {
          headerName: 'Named Place',
          field: "namedPlace",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('namedPlace')
          }
        },
        {
          headerName: 'From (Qty)',
          field: "fromQty",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('fromQty')
          }
        },
        {
          headerName: 'To (Qty)',
          field: "toQty",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('toQty')
          }
        },
        {
          headerName: 'Effective From',
          field: "effectiveStartDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('effectiveStartDate')
          },
          // provide a JS Date for AG Grid sorting/filtering
          valueGetter: (params) => {
            const v = params.data?.effectiveStartDate;
            if (!v || typeof v !== 'string') return null;
            const parts = v.split('-'); // expecting "DD-MM-YYYY"
            if (parts.length !== 3) return null;
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            const d = new Date(year, month, day);
            return isNaN(d.getTime()) ? null : d;
          },

          // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
          valueFormatter: (params) => {
            const src = params.data?.effectiveStartDate;
            if (src && typeof src === 'string') return src;
            if (params.value instanceof Date && !isNaN(params.value.getTime())) {
              const d = params.value;
              const dd = String(d.getDate()).padStart(2, '0');
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const yyyy = d.getFullYear();
              return `${dd}-${mm}-${yyyy}`;
            }
            return "-";
          },

          filter: "agDateColumnFilter",
          // sort by parsing the source string from the row data (robust even if other getters exist)
          sortComparator: (valueA, valueB, nodeA, nodeB) => {
            const aStr = nodeA?.data?.effectiveStartDate ?? valueA;
            const bStr = nodeB?.data?.effectiveStartDate ?? valueB;
            const parse = (s) => {
              if (!s || typeof s !== 'string') return null;
              const p = s.split('-');
              if (p.length !== 3) return null;
              const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
              return isNaN(d.getTime()) ? null : d.getTime();
            };
            const ta = parse(aStr);
            const tb = parse(bStr);
            if (ta === null && tb === null) return 0;
            if (ta === null) return -1;
            if (tb === null) return 1;
            return ta - tb;
          },
        },
        {
          headerName: 'Effective To',
          field: "effectiveEndDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('effectiveEndDate')
          },
          // provide a JS Date for AG Grid sorting/filtering
          valueGetter: (params) => {
            const v = params.data?.effectiveEndDate;
            if (!v || typeof v !== 'string') return null;
            const parts = v.split('-'); // expecting "DD-MM-YYYY"
            if (parts.length !== 3) return null;
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            const d = new Date(year, month, day);
            return isNaN(d.getTime()) ? null : d;
          },

          // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
          valueFormatter: (params) => {
            const src = params.data?.effectiveEndDate;
            if (src && typeof src === 'string') return src;
            if (params.value instanceof Date && !isNaN(params.value.getTime())) {
              const d = params.value;
              const dd = String(d.getDate()).padStart(2, '0');
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const yyyy = d.getFullYear();
              return `${dd}-${mm}-${yyyy}`;
            }
            return "-";
          },

          filter: "agDateColumnFilter",
          // sort by parsing the source string from the row data (robust even if other getters exist)
          sortComparator: (valueA, valueB, nodeA, nodeB) => {
            const aStr = nodeA?.data?.effectiveEndDate ?? valueA;
            const bStr = nodeB?.data?.effectiveEndDate ?? valueB;
            const parse = (s) => {
              if (!s || typeof s !== 'string') return null;
              const p = s.split('-');
              if (p.length !== 3) return null;
              const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
              return isNaN(d.getTime()) ? null : d.getTime();
            };
            const ta = parse(aStr);
            const tb = parse(bStr);
            if (ta === null && tb === null) return 0;
            if (ta === null) return -1;
            if (tb === null) return 1;
            return ta - tb;
          },
        },
        {
          headerName: 'Sap Payment Term',
          field: "sapPaymentTerm",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('sapPaymentTerm')
          }
        },
        {
          headerName: 'Manufacturing Lead Time',
          field: "manufacturingLeadTime",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('manufacturingLeadTime')
          }
        },
        {
          headerName: 'MOQ Comments',
          field: "moqComments",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell status-cell",
          cellRenderer: (params) => renderExpandableText(params, 50),
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('moqComments')
          }
        },
        {
          headerName: 'Per Qty',
          field: "perQty",
          sortable: true,
          resizable: false,
          suppressMovable: true,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('perQty')
          }
        },
        {
          headerName: 'Allocation Quantity',
          field: "allocationQty",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          cellClass: "status-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('allocationQty')
          }
        },
        {
          headerName: 'Allocation Notes',
          field: "allocationNotes",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('allocationNotes')
          }
        },
        {
          headerName: 'Forecast',
          field: "foreCastFile",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('foreCastFile')
          }
        },
        {
          headerName: 'Status',
          pinned: "right",
          field: "status",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('status')
          }
        },
        {
          headerName: 'Reject',
          pinned: "right",
          field: "reject",
          sortable: false,
          resizable: false,
          width: 120,
          filter: false,
          unSortIcon: true,
          cellRenderer: (params) => (
            <ButtonComponent
              maxWidth="100px"
              label="Reject"
              bgColor="black"
              color="white"
              disabled={
                params?.data?.status?.toLowerCase().includes("contract submitted") || 
                params?.data?.status?.toLowerCase().includes("contract creation failed") || 
                params?.data?.status?.toLowerCase().includes("contract update successful") || 
                params?.data?.status?.toLowerCase().includes("news") ||
                params?.data?.status?.toLowerCase().includes("contract simulation - success") ||
                params?.data?.status?.toLowerCase().includes("contract creation successful") ||
                params?.data?.status?.toLowerCase().includes("contract simulation - failed") 
                ? true : false}
              onClick={() => props.rejectHandler(params?.data?.runOutputSeqId)}
            />
          ),
        },
      ]
    } else if (props.data.cepgPriceMemo) {
      return [
        {
          headerName: 'Is New',
          field: "isNew",
          sortable: true,
          resizable: false,
          filterParams: {
            textFormatter: (value) => {
              if (typeof value === "string") {
                const v = value.trim().toLowerCase();
                if (v === "!") return "!";
                if (v === "-") return "-";
                return v;
              }
              return value;
            },
          },
          valueGetter: (params) => {
            if (params.data?.isNew === true) return "!";
            if (params.data?.isNew === false) return "-";
            return "";
          },
          valueFormatter: (params) => params.value,
          cellRenderer: (params) => {
            if (params.value === "!") {
              return (
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "20px",
                    textAlign: "center",
                  }}
                >
                  !
                </span>
              );
            }
            if (params.value === "-") {
              return (
                <span
                  style={{
                    color: "#616161",
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                >
                  -
                </span>
              );
            }
            return "";
          },
          pinned: "left",
          unSortIcon: true,
          width: 130,
        },
        {
          headerName: "Last Updated Time",
          field: "lastUpdateTimestamp",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          filter: "agSetColumnFilter",
          valueFormatter: (params) => formatTimestamp(params.value),
          filterParams: {
            valueFormatter: (params) => formatTimestamp(params.value),
          },
        },
        {
          headerName: 'SAP Plant Number',
          field: "plant",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('plant')
          }
        },
        {
          headerName: 'Plant Name',
          field: "plantName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('plantName')
          }
        },
        {
          headerName: 'SAP Material Number',
          field: "material",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('material')
          },
          cellRenderer: (params) => renderExpandableText(params, 50)
        },
        {
          headerName: 'SAP Vendor Number',
          field: "supplierNumber",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('supplierNumber')
          },
        },
        {
          headerName: 'Supplier Description',
          field: "description",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellRenderer: (params) => renderExpandableText(params, 50),
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('description')
          },
        },
        {
          headerName: 'Agreement Number',
          field: "agreementNumber",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('agreementNumber')
          }
        },
        {
          headerName: 'Agreement Date',
          field: "agreementDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('agreementDate')
          }
        },
        {
          headerName: 'Item Number',
          field: "agreementItemNumber",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('agreementItemNumber')
          }
        },
        {
          headerName: 'Package Size',
          field: "packSize",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('packSize')
          },
        },
        {
          headerName: 'S-Code',
          field: "scode",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('scode')
          },
        },
        {
          headerName: 'Year',
          field: "year",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('year')
          },
        },
        {
          headerName: 'Division',
          field: "division",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('division')
          },
        },
        {
          headerName: 'Forecast File',
          field: "foreCastFile",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('foreCastFile')
          },
        },
        {
          headerName: 'Manager',
          field: "manager",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('manager')
          },
        },
        {
          headerName: 'Supplier Name',
          field: "supplierName",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellRenderer: (params) => renderExpandableText(params, 50),
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('supplierName')
          },
        },
        {
          headerName: 'Gmp Lead',
          field: "gmpLead",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('gmpLead')
          },
        },
        {
          headerName: 'Change Date',
          field: "changeDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('changeDate')
          },
          // return a JS Date for AG Grid sorting/filtering
          valueGetter: (params) => {
            const v = params.data?.changeDate;
            if (!v) return null;
            const s = typeof v === 'string' ? v.replace(/\.00$/, '') : v;
            const d = new Date(s);
            return isNaN(d.getTime()) ? null : d;
          },

          // display as DD-MM-YYYY (prefer original string when available)
          valueFormatter: (params) => {
            const src = params.data?.changeDate;
            if (src && typeof src === 'string') {
              const s = src.replace(/\.00$/, '');
              const d = new Date(s);
              if (!isNaN(d.getTime())) {
                const dd = String(d.getDate()).padStart(2, '0');
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const yyyy = d.getFullYear();
                return `${dd}-${mm}-${yyyy}`;
              }
              return src;
            }
            if (params.value instanceof Date && !isNaN(params.value.getTime())) {
              const d = params.value;
              const dd = String(d.getDate()).padStart(2, '0');
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const yyyy = d.getFullYear();
              return `${dd}-${mm}-${yyyy}`;
            }
            return "-";
          },

          filter: "agDateColumnFilter",
          filterParams: {
            comparator: (filterLocalDateAtMidnight, cellValue, node) => {
              const src = node?.data?.changeDate ?? cellValue;
              if (!src) return -1;
              const s = typeof src === 'string' ? src.replace(/\.00$/, '') : src;
              const d = new Date(s);
              if (isNaN(d.getTime())) return -1;
              const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
              const filterYMD = new Date(
                filterLocalDateAtMidnight.getFullYear(),
                filterLocalDateAtMidnight.getMonth(),
                filterLocalDateAtMidnight.getDate()
              );
              if (cellYMD.getTime() === filterYMD.getTime()) return 0;
              return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
            },
            browserDatePicker: true,
          },

          // ensure sort parses the original string (works even when valueGetter removed elsewhere)
          sortComparator: (valueA, valueB, nodeA, nodeB) => {
            const aStr = nodeA?.data?.changeDate ?? valueA;
            const bStr = nodeB?.data?.changeDate ?? valueB;
            const aDate = aStr ? new Date((typeof aStr === 'string' ? aStr.replace(/\.00$/, '') : aStr)) : null;
            const bDate = bStr ? new Date((typeof bStr === 'string' ? bStr.replace(/\.00$/, '') : bStr)) : null;
            const ta = aDate && !isNaN(aDate.getTime()) ? aDate.getTime() : null;
            const tb = bDate && !isNaN(bDate.getTime()) ? bDate.getTime() : null;
            if (ta === null && tb === null) return 0;
            if (ta === null) return -1;
            if (tb === null) return 1;
            return ta - tb;
          },
        },
        {
          headerName: 'Inco Terms',
          field: "incoterms",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('incoterms')
          },
        },
        {
          headerName: 'Named Place',
          field: "namedPlace",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('namedPlace')
          },
        },
        {
          headerName: 'Price',
          field: "price",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('price')
          },
        },
        {
          headerName: 'Material Type',
          field: "material",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('material')
          },
        },
        {
          headerName: 'Currency',
          field: "currency",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('currency')
          },
        },
        {
          headerName: 'UOM',
          field: "uom",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('uom')
          },
        },
        {
          headerName: 'From (Qty)',
          field: "fromQty",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('fromQty')
          },
        },
        {
          headerName: 'To (Qty)',
          field: "toQty",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('toQty')
          },
        },
        {
          headerName: 'Effective From',
          field: "effectiveFrom",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('effectiveFrom')
          },
          // provide a JS Date for AG Grid sorting/filtering
          valueGetter: (params) => {
            const v = params.data?.effectiveFrom;
            if (!v || typeof v !== 'string') return null;
            const parts = v.split('-'); // expecting "DD-MM-YYYY"
            if (parts.length !== 3) return null;
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            const d = new Date(year, month, day);
            return isNaN(d.getTime()) ? null : d;
          },

          // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
          valueFormatter: (params) => {
            const src = params.data?.effectiveFrom;
            if (src && typeof src === 'string') return src;
            if (params.value instanceof Date && !isNaN(params.value.getTime())) {
              const d = params.value;
              const dd = String(d.getDate()).padStart(2, '0');
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const yyyy = d.getFullYear();
              return `${dd}-${mm}-${yyyy}`;
            }
            return "-";
          },

          filter: "agDateColumnFilter",
          // sort by parsing the source string from the row data (robust even if other getters exist)
          sortComparator: (valueA, valueB, nodeA, nodeB) => {
            const aStr = nodeA?.data?.effectiveFrom ?? valueA;
            const bStr = nodeB?.data?.effectiveFrom ?? valueB;
            const parse = (s) => {
              if (!s || typeof s !== 'string') return null;
              const p = s.split('-');
              if (p.length !== 3) return null;
              const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
              return isNaN(d.getTime()) ? null : d.getTime();
            };
            const ta = parse(aStr);
            const tb = parse(bStr);
            if (ta === null && tb === null) return 0;
            if (ta === null) return -1;
            if (tb === null) return 1;
            return ta - tb;
          },
        },
        {
          headerName: 'Effective To',
          field: "effectiveTo",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('effectiveTo')
          },
          // provide a JS Date for AG Grid sorting/filtering
          valueGetter: (params) => {
            const v = params.data?.effectiveTo;
            if (!v || typeof v !== 'string') return null;
            const parts = v.split('-'); // expecting "DD-MM-YYYY"
            if (parts.length !== 3) return null;
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            const d = new Date(year, month, day);
            return isNaN(d.getTime()) ? null : d;
          },

          // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
          valueFormatter: (params) => {
            const src = params.data?.effectiveTo;
            if (src && typeof src === 'string') return src;
            if (params.value instanceof Date && !isNaN(params.value.getTime())) {
              const d = params.value;
              const dd = String(d.getDate()).padStart(2, '0');
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const yyyy = d.getFullYear();
              return `${dd}-${mm}-${yyyy}`;
            }
            return "-";
          },

          filter: "agDateColumnFilter",
          // sort by parsing the source string from the row data (robust even if other getters exist)
          sortComparator: (valueA, valueB, nodeA, nodeB) => {
            const aStr = nodeA?.data?.effectiveTo ?? valueA;
            const bStr = nodeB?.data?.effectiveTo ?? valueB;
            const parse = (s) => {
              if (!s || typeof s !== 'string') return null;
              const p = s.split('-');
              if (p.length !== 3) return null;
              const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
              return isNaN(d.getTime()) ? null : d.getTime();
            };
            const ta = parse(aStr);
            const tb = parse(bStr);
            if (ta === null && tb === null) return 0;
            if (ta === null) return -1;
            if (tb === null) return 1;
            return ta - tb;
          },
        },
        {
          headerName: 'PIN',
          field: "pin",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('pin')
          },
        },
        {
          headerName: 'Alocation Quantity',
          field: "allocationQtyKg",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('allocationQtyKg')
          },
        },
        {
          headerName: 'Allocation Notes',
          field: "allocationNotes",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('allocationNotes')
          },
        },
        {
          headerName: 'Sap Payment Term',
          field: "paymentTerm",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('paymentTerm')
          },
        },
        {
          headerName: 'Picasso Facility',
          field: "picassoFacility",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "paymentTerms-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('picassoFacility')
          },
        },
        {
          headerName: 'Manufacturing Lead Time',
          field: "manufacturingLeadTime",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('manufacturingLeadTime')
          },
        },
        {
          headerName: 'MOQ Comments',
          field: "moqComments",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellRenderer: (params) => renderExpandableText(params, 50),
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('moqComments')
          },
        },
        {
          headerName: 'Error Details',
          field: "errorDetails",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellRenderer: (params) => renderExpandableText(params, 50),
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('errorDetails')
          },
        },
        {
          headerName: 'Open PO',
          field: "openPO",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('openPO')
          },
        },
        {
          headerName: 'Status',
          field: "status",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          initialWidth: 180,
          wrapText: true,
          autoHeight: true,
          cellClass: "status-cell",
          cellClassRules: {
            'bold-cell': (params) => params.data.changedFieldNames?.includes('status')
          },
        },
        {
          headerName: 'Reject',
          pinned: "right",
          field: "reject",
          sortable: false,
          resizable: false,
          width: 120,
          filter: false,
          unSortIcon: true,
          cellRenderer: (params) => (
            <ButtonComponent
              maxWidth="100px"
              label="Reject"
              bgColor="black"
              color="white"
              disabled={
                params?.data?.status?.toLowerCase().includes("contract submitted") || 
                params?.data?.status?.toLowerCase().includes("contract creation failed") || 
                params?.data?.status?.toLowerCase().includes("contract update successful") || 
                params?.data?.status?.toLowerCase().includes("news") ||
                params?.data?.status?.toLowerCase().includes("contract simulation - success") ||
                params?.data?.status?.toLowerCase().includes("contract creation successful") ||
                params?.data?.status?.toLowerCase().includes("contract simulation - failed") 
                ? true : false}
              onClick={() => props.rejectHandler(params?.data?.runOutputSeqId)}
            />
          ),
        },
      ]
    }
  }, [props.data])

  const selectionColumnDef = useMemo(() => ({
    pinned: "left",
  }), []);

  const gridApiRef = useRef(null); // Reference to the grid API

  
  const onColumnChange = e => {
    const col = e.target.value;
    setSelectedColumn(col);
    if (col && gridApiRef.current) {
      gridApiRef.current.ensureColumnVisible(col);
    }
  };

  const onGridReady = (params) => {
    gridApiRef.current = params.api; // Store the grid API instance
    setIsGridReady(true);
    if (columnState) {
      params.columnApi.applyColumnState({ state: columnState, applyOrder: true });
    }
  };

  useEffect(() => {
      // Restore filter model from localStorage on component mount
      if (isGridReady && gridApiRef?.current) {
        const savedFilterModel = localStorage.getItem("priceMemoFilterModel");
        if (savedFilterModel) {
          gridApiRef.current.setFilterModel(JSON.parse(savedFilterModel));
          setDisableFilterBtn(true); // Enable the "Clear All Filters" button
        }
      }
    }, [isGridReady]);

  const clearAllFilters = () => {
    if (gridApiRef.current) {
      gridApiRef.current.setFilterModel(null); // Clear all filters
      localStorage.removeItem("priceMemoFilterModel");
      setDisableFilterBtn(true);
    }
  };

  const onFilterChanged = (params) => {
    if (gridApiRef.current) {
      const filterModel = gridApiRef.current.getFilterModel();
      setDisableFilterBtn(Object.keys(filterModel).length === 0); // Enable button if filters exist
      setFilteredTotalColumn(Object.keys(filterModel).length);

      const filteredRowCount = params.api.getDisplayedRowCount();
      localStorage.setItem("priceMemoFilterModel", JSON.stringify(filterModel));
      setFilteredTotalItems(filteredRowCount);
      props.setPMFilteredData(gridApiRef.current.getDisplayedRowCount());
    }
  };

  const onColumnMoved = useCallback((params) => {
    if (params && params.columnApi && typeof params.columnApi.getColumnState === "function") {
      setColumnState(params.columnApi.getColumnState());
    }
  }, []);

  const gridOptions = {
    getRowId: (params) => {
      return `${params?.data?.runOutputSeqId}`;
    },
    overlayLoadingTemplate: `<span class="ag--overlay-loading-center" >Please wait while data is loading</span>`,
    overlayNoRowsTemplate: `<span class="ag--overlay-no-rows-center" >No Data Available</span>`,
    defaultColDef: {
      sortable: true,
      filter: 'agMultiColumnFilter',
      autoHeight: true,
      cellStyle: {
        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: 1.5,
        display: "flex",
        alignItems: "center",
      },
      valueGetter: (params) => {
        if (!params.data) return "-";

        const field = params.colDef.field;
        const value = params.data[field];

        return value === null || value === undefined || value === ""
          ? "-"
          : value;
      },
    },
    onColumnMoved,
    onFilterChanged,
  };

  const getContextMenuItems = (params) => {
    const defaultItems = params.defaultItems || [];

    // Remove the 'export' option
    return defaultItems.filter(item => item !== 'export');
  };

  return (
    <>
      <div style={{display: 'flex', marginBottom: '20px', justifyContent: 'space-between', alignItems: 'center'}}>
        <FormControl sx={{ m: 1, minWidth: 200 }} size="small">
          <InputLabel>Jump to Column</InputLabel>
          <Select
            id="col-select"
            value={selectedColumn}
            label="Jump to Column"
            onChange={onColumnChange}
          >
            <MenuItem value="">Select Column</MenuItem>
            {columnData?.map(col => (
              <MenuItem key={col?.field} value={col?.field}>
                {col?.headerName || col?.field}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <ButtonComponent
          maxWidth="150px"
          label="Clear All Filters"
          bgColor="black"
          color="white"
          disabled={disableFilterBtn}
          onClick={clearAllFilters}
        />
      </div>
      <DataTable
        height={750}
        columns={columnData}
        rows={props?.data?.sisPriceMemo ||
          props?.data?.agriPriceMemo ||
          props?.data?.packagingPriceMemo ||
          props?.data?.cepgPriceMemo || []}
        onRowSelected={props.onRowSelected}
        onSelectionChanged={props.onSelectionChanged}
        rowSelection={props.rowSelection}
        getRowStyle={props.getRowStyle}
        selectionColumnDef={selectionColumnDef}
        onCellValueChanged={props.handleCellValueChange}
        onGridReady={onGridReady}
        paginationProp={false}
        gridOptions={gridOptions}
        postFilterChanged={handlePostFilterChanged}
        filteredTotalColumn={filteredTotalColumn}
        getContextMenuItems={getContextMenuItems}
      />
    </>
  );
};

export default DataDetails;
